/**
 * Rainbow Mulch — Playground Project Builder
 * FREIGHT FALLBACK RANGE (D-042, resolves OQ-15)
 *
 * ============================================================================
 *  WHAT THIS IS FOR
 * ============================================================================
 * When the live R+L call fails (timeout, outage, unquotable lane), Step 4 shows
 * an ESTIMATED RANGE instead of a number. D-004 forbids inventing that range,
 * so until now it could not ship: pb-mocks.js carried a made-up $700–$1,000.
 *
 * This module replaces that guess with a model fitted to 114 real historical
 * orders (data/historical-freight-orders.csv, supplied 2026-08-17).
 *
 * ⚠️ THIS RANGE IS INFORMATIONAL AND MUST NEVER BE CHARGED.
 * SH-18 requires the amount actually billed to come from a live server-side
 * quote. This module produces a display range for the failure path only. It is
 * deliberately safe to run in the browser BECAUSE it never becomes a line item
 * — if that ever changes, this module must move server-side first.
 *
 * ============================================================================
 *  WHY A MODEL RATHER THAN THE DISTANCE BANDS THAT WERE ASKED FOR
 * ============================================================================
 * The intent was distance bands. The data says distance is the WEAKER driver:
 *
 *   corr(cost, weight)   = +0.93        corr(cost, distance) = +0.25
 *
 * And $/lb is not constant — it falls roughly threefold as orders get heavier
 * (bulk: $0.78/lb light, $0.51 mid, $0.29 heavy). So any flat "$/lb × weight"
 * band, however finely split by distance, systematically OVERCHARGES the large
 * orders this builder exists to quote. A power law captures that curve:
 *
 *   cost ≈ coefficient × weight^0.535 × miles^0.394
 *
 * Exponents below 1 on both terms ARE the economies of scale. Fitted R² = 0.81
 * on 96 LTL orders.
 *
 * Discrete bands were also rejected because 4 distance bands × 3 weight bands
 * × 2 packaging types is 24 cells for 96 orders — around 4 orders each, and
 * cliff edges where one mile across a boundary jumps the estimate. The
 * continuous fit uses all 96 observations for every prediction.
 *
 * ============================================================================
 *  WHY PACKAGING IS *NOT* A PARAMETER — this contradicts the original brief
 * ============================================================================
 * The brief said bulk and bagged have materially different $/lb (~$0.45 vs
 * ~$0.96 median) and must be kept separate. Those medians are correct, but the
 * difference is a CONFOUND, not a packaging effect:
 *
 *   - Bagged orders are systematically smaller (median 240 lb vs 1,400 lb), and
 *     small shipments always cost more per pound.
 *   - 12 of 41 bagged orders went UPS PARCEL, where $/lb is highest of all.
 *
 * Compared at overlapping LTL weights (380–3,500 lb) the gap nearly vanishes,
 * and reverses:
 *
 *   bulk   n=54  median $0.477/lb
 *   bagged n=16  median $0.376/lb
 *
 * Adding a packaging dummy to the fitted model moves R² from 0.8084 to 0.8087,
 * a coefficient of 0.97× — F(1,92) = 0.13, nowhere near significant. Splitting
 * on packaging would therefore halve the data behind each estimate to model
 * noise. Physically this is expected: 1,728 lb of bags and 1,758 lb of totes
 * are both palletised class-50 LTL freight. The carrier prices weight, class
 * and distance — not what the material is sitting in.
 *
 * ============================================================================
 *  WHAT THIS MODEL CANNOT DO — read before trusting it
 * ============================================================================
 * 1. NO ACCESSORIAL AWARENESS. The historical data does not record which
 *    deliveries were residential or needed a liftgate, so the model averages
 *    over an unknown mix. Validated against the five real R+L quotes from
 *    D-041, four of five land inside the band; the miss is the one COMMERCIAL
 *    no-liftgate lane ($156.20 actual vs $292 fit, 0.53×) — the cheapest
 *    possible case, which the band's low end does not reach. Expect the
 *    estimate to run high for commercial deliveries.
 *
 * 2. MIXED CARRIERS. Mostly R&L, some WARD/UPS/TQL, and 49 rows with no
 *    carrier recorded. This is a real-world baseline, not R+L contract pricing.
 *
 * 3. STATE-CENTROID GEOGRAPHY. Distance is origin → destination STATE centroid,
 *    so every Texas ZIP is treated as equidistant though the state spans ~800
 *    miles. Refining to ZIP3 centroids needs a dataset we do not have.
 *
 * 4. THE LOCAL BAND IS THIN. Only 11 of 96 LTL orders fall within 300 miles
 *    (OH/IN/KY/IL/WV). Estimates there are flagged low-confidence.
 *
 * 5. PARCEL IS EXCLUDED BY DESIGN. The 12 UPS orders were dropped because our
 *    integration always quotes R+L LTL at class 50. Historical small orders
 *    that shipped UPS cheaply are not what we would now charge, so including
 *    them would bias the estimate below our own quoting path.
 *
 * 6. NO SUPPORT ABOVE ~7,000 LBS (D-070, 2026-09-04). The heaviest of the 96
 *    fitted orders tops out there; the only two historical orders over 16,000
 *    lbs (26,847 and 33,212 lbs) are non-LTL truckload, excluded from the fit
 *    entirely. `estimateRange()` returns null above `PBConfig.freight.
 *    maxQuotableWeightLbs` (16,000 lbs, matching R+L's own API ceiling as of
 *    2026-09-07) rather than extrapolate the curve past where it has ever
 *    been tested against a real order.
 *
 * Re-fit when the KV rate history (recordRateHistory) has accumulated enough
 * genuine R+L quotes to stand on its own. That dataset records accessorials and
 * exact destination ZIPs, so it can fix limitations 1, 2 and 3 together.
 */

(function () {
  'use strict';

  var cfg = window.PBConfig;

  /**
   * ZIP3 prefix → state. Range-based because USPS allocates prefixes
   * geographically. Reference geography, not a business rule.
   */
  var ZIP3_RANGES = [
    ['005', '005', 'NY'], ['006', '009', 'PR'], ['010', '027', 'MA'], ['028', '029', 'RI'],
    ['030', '038', 'NH'], ['039', '049', 'ME'], ['050', '059', 'VT'], ['060', '069', 'CT'],
    ['070', '089', 'NJ'], ['100', '149', 'NY'], ['150', '196', 'PA'], ['197', '199', 'DE'],
    ['200', '205', 'DC'], ['206', '219', 'MD'], ['220', '246', 'VA'], ['247', '268', 'WV'],
    ['270', '289', 'NC'], ['290', '299', 'SC'], ['300', '319', 'GA'], ['320', '349', 'FL'],
    ['350', '369', 'AL'], ['370', '385', 'TN'], ['386', '397', 'MS'], ['398', '399', 'GA'],
    ['400', '427', 'KY'], ['430', '459', 'OH'], ['460', '479', 'IN'], ['480', '499', 'MI'],
    ['500', '528', 'IA'], ['530', '549', 'WI'], ['550', '567', 'MN'], ['570', '577', 'SD'],
    ['580', '588', 'ND'], ['590', '599', 'MT'], ['600', '629', 'IL'], ['630', '658', 'MO'],
    ['660', '679', 'KS'], ['680', '693', 'NE'], ['700', '714', 'LA'], ['716', '729', 'AR'],
    ['730', '749', 'OK'], ['750', '799', 'TX'], ['800', '816', 'CO'], ['820', '831', 'WY'],
    ['832', '838', 'ID'], ['840', '847', 'UT'], ['850', '865', 'AZ'], ['870', '884', 'NM'],
    ['889', '898', 'NV'], ['900', '961', 'CA'], ['967', '968', 'HI'], ['970', '979', 'OR'],
    ['980', '994', 'WA'], ['995', '999', 'AK']
  ];

  /** State centroids [lat, lon]. Reference geography. */
  var CENTROID = {
    AL: [32.79, -86.83], AZ: [34.29, -111.66], AR: [34.90, -92.44], CA: [37.18, -119.47],
    CO: [39.00, -105.55], CT: [41.62, -72.73], DE: [38.99, -75.51], FL: [28.63, -82.45],
    GA: [32.65, -83.44], ID: [44.39, -114.66], IL: [40.04, -89.20], IN: [39.91, -86.28],
    IA: [42.07, -93.50], KS: [38.49, -98.38], KY: [37.53, -85.30], LA: [31.07, -91.96],
    ME: [45.37, -69.24], MD: [39.04, -76.79], MA: [42.26, -71.81], MI: [44.35, -85.41],
    MN: [46.28, -94.31], MS: [32.74, -89.68], MO: [38.37, -92.48], MT: [47.05, -109.63],
    NE: [41.53, -99.81], NV: [39.35, -116.63], NH: [43.68, -71.58], NJ: [40.19, -74.67],
    NM: [34.42, -106.11], NY: [42.95, -75.53], NC: [35.56, -79.39], ND: [47.45, -100.47],
    OH: [40.29, -82.79], OK: [35.59, -97.49], OR: [43.93, -120.56], PA: [40.88, -77.80],
    RI: [41.68, -71.56], SC: [33.92, -80.90], SD: [44.44, -100.23], TN: [35.86, -86.35],
    TX: [31.48, -99.33], UT: [39.34, -111.65], VT: [44.07, -72.67], VA: [37.52, -78.85],
    WA: [47.38, -120.45], WV: [38.64, -80.62], WI: [44.62, -89.99], WY: [42.99, -107.55],
    DC: [38.90, -77.02]
  };

  /**
   * States we have NO fitted support for. AK/HI/PR are not LTL-comparable
   * lanes at all — they need an ocean or air leg. Returning null for these is
   * correct behaviour, not a gap: D-004 forbids inventing a number.
   */
  var UNSUPPORTED = { AK: true, HI: true, PR: true };

  /** ZIP3 → two-letter state, or null when unrecognised. */
  function stateForZip(zip) {
    if (typeof zip !== 'string') return null;
    var z = zip.trim();
    if (!/^\d{5}$/.test(z)) return null;
    var p = z.slice(0, 3);
    for (var i = 0; i < ZIP3_RANGES.length; i++) {
      if (p >= ZIP3_RANGES[i][0] && p <= ZIP3_RANGES[i][1]) return ZIP3_RANGES[i][2];
    }
    return null;
  }

  /** Great-circle miles between two [lat, lon] pairs. */
  function haversineMiles(from, to) {
    var rad = Math.PI / 180;
    var dLat = (to[0] - from[0]) * rad;
    var dLon = (to[1] - from[1]) * rad;
    var h = Math.pow(Math.sin(dLat / 2), 2) +
      Math.cos(from[0] * rad) * Math.cos(to[0] * rad) * Math.pow(Math.sin(dLon / 2), 2);
    return 2 * 3958.8 * Math.asin(Math.sqrt(h));
  }

  /** Straight-line miles from the yard to a destination ZIP, or null. */
  function milesToZip(zip) {
    var st = stateForZip(zip);
    if (!st || UNSUPPORTED[st] || !CENTROID[st]) return null;
    var m = cfg.freight.fallbackModel;
    return Math.max(haversineMiles([m.originLat, m.originLon], CENTROID[st]), m.minDistanceMiles);
  }

  /**
   * Which fitted distance band a mileage falls in, for the confidence flag.
   * Bands exist ONLY to report data sufficiency — they are not used to price.
   */
  function bandFor(miles) {
    var bands = cfg.freight.fallbackModel.bands;
    for (var i = 0; i < bands.length; i++) {
      if (miles < bands[i].maxMiles) return bands[i];
    }
    return bands[bands.length - 1];
  }

  /**
   * Estimate a freight RANGE for a shipment.
   *
   * @param {number} weightLbs  total shipment weight (from PBCalculator, D-032)
   * @param {string} zip        destination ZIP
   * @returns {null|{low:number, high:number, point:number, miles:number,
   *                 state:string, confidence:'ok'|'low', sampleSize:number,
   *                 band:string}}
   *          null when no defensible estimate exists — an unrecognised ZIP, a
   *          non-LTL lane (AK/HI/PR), or a nonsensical weight. Callers MUST
   *          handle null by showing no range rather than substituting a guess.
   */
  function estimateRange(weightLbs, zip) {
    if (typeof weightLbs !== 'number' || !isFinite(weightLbs) || weightLbs <= 0) return null;

    /**
     * D-070 — no range above R+L's quotable weight ceiling (16,000 lbs as of
     * 2026-09-07). Two reasons stack here, not one:
     *   1. Above that line R+L's own API won't quote it, so the number could
     *      never become a live quote — same category as AK/HI/PR below.
     *   2. Independently, this fit's heaviest real training order (excluding
     *      two non-LTL truckload outliers over 26,000 lbs) is ~7,000 lbs.
     *      Extrapolating the curve out to 16,000+ lbs would produce a number
     *      that LOOKS as fitted as every other range this function returns,
     *      and is not — a business decision to withhold it, not a data gap
     *      we could paper over with a wider band.
     * D-004 governs either way: return null rather than a guess.
     */
    var ceiling = cfg.freight.maxQuotableWeightLbs;
    if (typeof ceiling === 'number' && weightLbs > ceiling) return null;

    var miles = milesToZip(zip);
    if (miles === null) return null;

    var m = cfg.freight.fallbackModel;
    var point = m.coefficient *
      Math.pow(weightLbs, m.weightExponent) *
      Math.pow(miles, m.distanceExponent);

    if (!isFinite(point) || point <= 0) return null;

    var band = bandFor(miles);

    return {
      point: round2(point),
      low: round2(point * m.lowMultiplier),
      high: round2(point * m.highMultiplier),
      miles: Math.round(miles),
      state: stateForZip(zip),
      band: band.id,
      sampleSize: band.sampleSize,
      // Surfaced so the UI can hedge harder where the fit rests on few orders.
      confidence: band.sampleSize >= m.minSampleForConfidence ? 'ok' : 'low'
    };
  }

  function round2(x) {
    return Math.round(x * 100) / 100;
  }

  window.PBFreightFallback = {
    estimateRange: estimateRange,
    milesToZip: milesToZip,
    stateForZip: stateForZip,
    haversineMiles: haversineMiles
  };
})();
